import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cn } from './utils/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      hint,
      error,
      startIcon,
      endIcon,
      id,
      className,
      containerClassName,
      required,
      ...rest
    },
    ref,
  ) => {
    const reactId = useId();
    const inputId = id ?? reactId;
    const describedById = error ? `${inputId}-err` : hint ? `${inputId}-hint` : undefined;

    return (
      <div className={cn('flex flex-col gap-1.5', containerClassName)}>
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-fg">
            {label}
            {required ? <span className="text-danger ms-0.5">*</span> : null}
          </label>
        )}
        <div
          className={cn(
            'flex items-center gap-2 h-11 px-3 rounded-md',
            'bg-surface border border-border',
            'transition-colors duration-150',
            'focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/30',
            error && 'border-danger focus-within:border-danger focus-within:ring-danger/30',
          )}
        >
          {startIcon ? <span className="text-fg-subtle shrink-0">{startIcon}</span> : null}
          <input
            ref={ref}
            id={inputId}
            aria-invalid={!!error}
            aria-describedby={describedById}
            required={required}
            className={cn(
              'flex-1 min-w-0 bg-transparent outline-none text-sm',
              'placeholder:text-fg-subtle disabled:cursor-not-allowed disabled:opacity-60',
              className,
            )}
            {...rest}
          />
          {endIcon ? <span className="text-fg-subtle shrink-0">{endIcon}</span> : null}
        </div>
        {error ? (
          <p id={`${inputId}-err`} className="text-xs text-danger">
            {error}
          </p>
        ) : hint ? (
          <p id={`${inputId}-hint`} className="text-xs text-fg-subtle">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
